import { AxiosError } from 'axios'
import { computed, flow, makeObservable, observable } from 'mobx'

import { openPdfDocumentFromBlob, requestWrapper } from 'Shared/utils'

import { STORE_UI } from 'Core/constants'
import { searchQueryConfig } from 'Core/constants/filters'
import { CoreUrlParams, CustomGenerator, SortingParams } from 'Core/interfaces'
import { TableStore } from 'Core/stores/tableStore/TableStore'
import { emptyCustomLabelValue, entityToLabelWithDashValue } from 'Core/utils'
import { getDefaultStoreArrayValue, storagePageSizeConstant } from 'Core/utils/storeHelpers'

import { UrlParams } from 'Portals/Customer/constants'
import { Manifest } from 'Portals/Customer/entities'
import { searchConfig } from 'Portals/Customer/Pages/Manifest/TableList/Filters/utils/config'
import { ManifestRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'

const STORAGE_MANIFEST_PAGE_SIZE = storagePageSizeConstant('manifest')

export class ManifestTable extends TableStore<Manifest.Basic> {
	@observable public accessor serviceSites = getDefaultStoreArrayValue<Entity>()

	constructor(public rootStore: RootStore) {
		super(STORAGE_MANIFEST_PAGE_SIZE)
	}

	@flow.bound
	*getItems(defaultSort?: SortingParams) {
		try {
			yield this.requestData({
				repository: ManifestRepo.getList,
				defaultSort
			})
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*retrieveServiceSites() {
		yield requestWrapper({
			field: this.serviceSites,
			repository: ManifestRepo.retrieveManifestServiceSites,
			repositoryParams: null
		})
	}

	@computed
	get serviceSitesOptions(): LabelValue[] {
		return [
			emptyCustomLabelValue('All'),
			...this.serviceSites.value.map(entityToLabelWithDashValue)
		]
	}

	@flow.bound
	*downloadManifestFile(manifestDocumentId: Key, name?: BasicString): CustomGenerator<Blob> {
		try {
			const document = yield ManifestRepo.downloadManifestFile(manifestDocumentId)
			openPdfDocumentFromBlob(document, name)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	initFromURLParams = (
		params: Record<string, string[]>,
		filtersConfig: Manifest.Filter.Config
	) => {
		const searchKeys = [
			UrlParams.disposalFacility,
			UrlParams.manifestDocumentNumber,
			CoreUrlParams.searchQuery
		]
		const filterKeys = [UrlParams.serviceSiteId, UrlParams.startDate, UrlParams.endDate]

		const mergedSearchConfig = {
			...searchConfig,
			...searchQueryConfig
		}
		this.initSearchParamsFromUrl(params, searchKeys, mergedSearchConfig)
		this.initFilterParamsFromUrl(params, filterKeys, filtersConfig)
	}

	reset = () => {
		this.resetTable()
		this.retrieveServiceSites()
	}
}
